import { useEffect, useState, useCallback } from "react";
import { api, errMsg, STATUS_META, pad, itDate } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const EmployeeSummary = ({ employeeId, year, month, onClose }) => {
  const lastDay = new Date(year, month, 0).getDate();
  const [from, setFrom] = useState(`${year}-${pad(month)}-01`);
  const [to, setTo] = useState(`${year}-${pad(month)}-${pad(lastDay)}`);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    api.get(`/attendance/summary/${employeeId}`, { params: { date_from: from, date_to: to } })
      .then((r) => { setData(r.data); setErr(""); })
      .catch((e) => setErr(errMsg(e)));
  }, [employeeId, from, to]);

  useEffect(() => { load(); }, [load]);

  const e = data?.employee;
  const s = data?.summary;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-sm sm:max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="employee-summary">
        <DialogHeader>
          <DialogTitle className="font-head">
            {e ? `${e.last_name} ${e.first_name}` : "Riepilogo"}
            {e && (
              <div className="text-xs font-normal text-muted-foreground mt-0.5 tabular-nums">
                {e.code} · {e.department} · {e.role_title} · Assunto il {e.hire_date || "—"} ·{" "}
                <span className={e.active ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}>
                  {e.active ? "ATTIVO" : "NON ATTIVO"}
                </span>
              </div>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Dal</Label>
            <Input type="date" value={from} onChange={(ev) => setFrom(ev.target.value)} className="h-9 rounded-sm mt-1" data-testid="summary-from" />
          </div>
          <div>
            <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Al</Label>
            <Input type="date" value={to} onChange={(ev) => setTo(ev.target.value)} className="h-9 rounded-sm mt-1" data-testid="summary-to" />
          </div>
        </div>

        {err && <div className="rounded-sm border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">{err}</div>}

        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {Object.keys(STATUS_META).map((k) => (
            <div key={k} data-testid={`summary-${k}`} className="rounded-sm border border-border p-2.5">
              <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                <span className={`h-2 w-2 rounded-full ${STATUS_META[k].dot}`} />{k}
              </div>
              <div className="text-xl font-head font-bold tabular-nums mt-1">{s?.[k] ?? "—"}</div>
            </div>
          ))}
          <div className="rounded-sm border border-primary/40 bg-accent p-2.5">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-accent-foreground">Giorni lavorati</div>
            <div className="text-xl font-head font-bold tabular-nums mt-1" data-testid="summary-worked-days">{s?.worked_days ?? "—"}</div>
          </div>
          <div className="rounded-sm border border-primary/40 bg-accent p-2.5">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-accent-foreground">Giorni registrati</div>
            <div className="text-xl font-head font-bold tabular-nums mt-1" data-testid="summary-registered">{s?.registered_days ?? "—"}</div>
          </div>
        </div>

        <div className="rounded-sm border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary">
              <tr>
                {["Data", "Causale", "Note"].map((h) => (
                  <th key={h} className="px-2 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data?.records || []).map((r) => (
                <tr key={r.date} className="border-t border-border">
                  <td className="px-2 py-1 tabular-nums text-xs">{itDate(r.date)}</td>
                  <td className="px-2 py-1">
                    <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-bold ${STATUS_META[r.status]?.cell}`}>{r.status}</span>
                  </td>
                  <td className="px-2 py-1 text-xs text-muted-foreground">{r.notes || ""}</td>
                </tr>
              ))}
              {data && data.records.length === 0 && (
                <tr><td colSpan={3} className="px-2 py-6 text-center text-sm text-muted-foreground">Nessuna registrazione nel periodo</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EmployeeSummary;
